import {
    HubConnectionBuilder,
    HubConnection,
    LogLevel,
    HttpTransportType,
    HubConnectionState,
} from "@microsoft/signalr";
import { notifyBugsnag } from "@/lib/bugsnag";
import logger from "@/utils/logger";

let connection: HubConnection | null = null;
let retryCount = 0;
const maxRetries = 3;
let isConnecting = false;
let connectionFailed = false;
let connectionPromise: Promise<void> | null = null;
let isStoppingConnection = false;
let currentConnectionEmail: string | null = null;
let currentConnectionHandlerName: string | null = null;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
let outageStartedAt: number | null = null;
let outageReportTimeout: ReturnType<typeof setTimeout> | null = null;
let hasReportedProlongedOutage = false;

const SIGNALR_OUTAGE_GRACE_MS = 5 * 60 * 1000;

interface SignalROutageContext {
    source: "reconnecting" | "close" | "start" | "logger";
    handlerName: string;
    email: string;
    error?: Error;
}

let latestOutageContext: SignalROutageContext | null = null;

interface NamedHandler<T> {
    name: string;
    handler: (data: T) => void;
}

export interface SignalRReconnectEvent {
    reason: "reconnected" | "recovered";
    retryCount: number;
}

let notificationHandlers: Array<NamedHandler<unknown>> = [];
let tripStatusHandlers: Array<
    NamedHandler<{
        tripId: number;
        newStatus: string;
        oldStatus: string | null;
        changedAt: string;
        changedBy?: number;
    }>
> = [];
let reconnectHandlers: Array<NamedHandler<SignalRReconnectEvent>> = [];

const retryDelays = [0, 2000, 10000, 30000];

const getConnectionState = (): HubConnectionState | undefined => {
    return (connection as Partial<HubConnection>)?.state as
        | HubConnectionState
        | undefined;
};

const hasActiveHandlers = () =>
    notificationHandlers.length > 0 || tripStatusHandlers.length > 0;

const clearReconnectTimeout = () => {
    if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
    }
};

const clearOutageReportTimeout = () => {
    if (outageReportTimeout) {
        clearTimeout(outageReportTimeout);
        outageReportTimeout = null;
    }
};

const clearOutageState = () => {
    outageStartedAt = null;
    latestOutageContext = null;
    hasReportedProlongedOutage = false;
    connectionFailed = false;
    clearOutageReportTimeout();
};

const reportProlongedOutage = () => {
    if (
        hasReportedProlongedOutage ||
        !latestOutageContext ||
        !hasActiveHandlers()
    ) {
        return;
    }

    hasReportedProlongedOutage = true;
    connectionFailed = true;

    const outageMs = outageStartedAt ? Date.now() - outageStartedAt : 0;
    const error =
        latestOutageContext.error ??
        new Error("SignalR connection unavailable for more than 5 minutes.");

    logger.error("SignalR: Connection unavailable for more than 5 minutes", {
        source: latestOutageContext.source,
        handlerName: latestOutageContext.handlerName,
        email: latestOutageContext.email,
        retryCount,
        outageMs,
    });

    notifyBugsnag(error, {
        kind: "signalr-prolonged-outage",
        source: latestOutageContext.source,
        handlerName: latestOutageContext.handlerName,
        email: latestOutageContext.email,
        retryCount,
        outageMs,
    });
};

const beginOutageTracking = (context: SignalROutageContext) => {
    if (!hasActiveHandlers()) {
        return;
    }

    if (outageStartedAt === null) {
        outageStartedAt = Date.now();
    }

    latestOutageContext = context;

    if (outageReportTimeout || hasReportedProlongedOutage) {
        return;
    }

    const elapsedMs = Date.now() - outageStartedAt;
    const remainingMs = Math.max(0, SIGNALR_OUTAGE_GRACE_MS - elapsedMs);

    outageReportTimeout = setTimeout(() => {
        outageReportTimeout = null;

        if (getConnectionState() === HubConnectionState.Connected) {
            clearOutageState();
            return;
        }

        reportProlongedOutage();
    }, remainingMs);
};

const isTransientSignalRDisconnectMessage = (message: string) =>
    /connection disconnected with error|server returned an error on close|websocket closed with status code|failed to start the transport|the connection was closed before the hub handshake completed/i.test(
        message
    );

const signalRLogger = {
    log(logLevel: LogLevel, message: string) {
        const text = `${message}`;

        if (logLevel >= LogLevel.Warning) {
            if (isTransientSignalRDisconnectMessage(text)) {
                beginOutageTracking({
                    source: "logger",
                    handlerName: "SignalRLogger",
                    email: currentConnectionEmail || "unknown",
                    error: new Error(text),
                });

                if (hasReportedProlongedOutage) {
                    logger.error("SignalR:", text);
                } else {
                    logger.info("SignalR: transient disconnect detected", text);
                }
                return;
            }

            if (logLevel >= LogLevel.Error && hasReportedProlongedOutage) {
                logger.error("SignalR:", text);
            } else {
                logger.warn("SignalR:", text);
            }
            return;
        }

        logger.info("SignalR:", text);
    },
};

const removeHandler = <T>(
    handlers: Array<NamedHandler<T>>,
    handlerToRemove: ((data: T) => void) | undefined
) => {
    if (!handlerToRemove) {
        return handlers;
    }

    return handlers.filter((entry) => entry.handler !== handlerToRemove);
};

const notifyReconnectHandlers = (event: SignalRReconnectEvent) => {
    reconnectHandlers.forEach((namedHandler) => {
        try {
            namedHandler.handler(event);
        } catch (error) {
            console.error(
                `SignalR: ❌ Error in reconnect handler "${namedHandler.name}":`,
                error
            );
        }
    });
};

const scheduleReconnect = (delayMs: number, context: string) => {
    const email = currentConnectionEmail;

    if (
        reconnectTimeout ||
        isStoppingConnection ||
        !email ||
        !hasActiveHandlers()
    ) {
        return;
    }

    reconnectTimeout = setTimeout(() => {
        reconnectTimeout = null;
        void ensureConnection(
            email,
            currentConnectionHandlerName || context || "Unknown"
        );
    }, delayMs);
};

const registerHandlers = (
    onNotification: (notification: unknown) => void,
    onTripStatusChanged:
        | ((data: {
              tripId: number;
              newStatus: string;
              oldStatus: string | null;
              changedAt: string;
              changedBy?: number;
          }) => void)
        | undefined,
    handlerName: string
) => {
    let registeredNotification = false;
    let registeredTripStatus = false;

    const existingNotificationHandler = notificationHandlers.find(
        (entry) => entry.handler === onNotification
    );
    if (!existingNotificationHandler) {
        notificationHandlers.push({
            name: handlerName,
            handler: onNotification,
        });
        registeredNotification = true;
        logger.info(
            `SignalR: ✅ Registered notification handler "${handlerName}" (total: ${notificationHandlers.length})`
        );
    }

    if (onTripStatusChanged) {
        const existingTripStatusHandler = tripStatusHandlers.find(
            (entry) => entry.handler === onTripStatusChanged
        );
        if (!existingTripStatusHandler) {
            tripStatusHandlers.push({
                name: handlerName,
                handler: onTripStatusChanged,
            });
            registeredTripStatus = true;
            logger.info(
                `SignalR: ✅ Registered trip status handler "${handlerName}" (total: ${tripStatusHandlers.length})`
            );
        }
    }

    return () => {
        if (registeredNotification) {
            notificationHandlers = removeHandler(
                notificationHandlers,
                onNotification
            );
        }

        if (registeredTripStatus) {
            tripStatusHandlers = removeHandler(
                tripStatusHandlers,
                onTripStatusChanged
            );
        }

        if (!hasActiveHandlers()) {
            currentConnectionEmail = null;
            currentConnectionHandlerName = null;
            clearReconnectTimeout();
            void disconnectFromNotificationHub();
        }
    };
};

const attachConnectionHandlers = (
    activeConnection: HubConnection,
    handlerName: string,
    email: string
) => {
    activeConnection.serverTimeoutInMilliseconds = 120_000;
    activeConnection.keepAliveIntervalInMilliseconds = 30_000;

    activeConnection.onreconnected(() => {
        logger.info("SignalR: Reconnected successfully");
        const reconnectEvent: SignalRReconnectEvent = {
            reason: "reconnected",
            retryCount,
        };
        retryCount = 0;
        clearOutageState();
        notifyReconnectHandlers(reconnectEvent);
    });

    activeConnection.onreconnecting((error) => {
        logger.info("SignalR: Attempting to reconnect...", error?.message);
        beginOutageTracking({
            source: "reconnecting",
            handlerName,
            email,
            error,
        });
    });

    activeConnection.onclose((error) => {
        connection = null;
        connectionPromise = null;
        isConnecting = false;

        if (isStoppingConnection || !hasActiveHandlers()) {
            return;
        }

        retryCount += 1;
        beginOutageTracking({
            source: "close",
            handlerName,
            email,
            error: error ?? undefined,
        });

        scheduleReconnect(retryCount >= maxRetries ? 15_000 : 2_000, "close");
    });

    activeConnection.on("Dispatch IQ", (notification: unknown) => {
        try {
            notificationHandlers.forEach((namedHandler) => {
                try {
                    namedHandler.handler(notification);
                } catch (error) {
                    console.error(
                        `SignalR: ❌ Error in handler "${namedHandler.name}":`,
                        error
                    );
                }
            });
        } catch (error) {
            console.error("SignalR: Error handling notification:", error);
        }
    });

    activeConnection.on(
        "TripStatusChanged",
        (data: {
            tripId: number;
            newStatus: string;
            oldStatus: string | null;
            changedAt: string;
            changedBy?: number;
        }) => {
            try {
                tripStatusHandlers.forEach((namedHandler) => {
                    try {
                        namedHandler.handler(data);
                    } catch (error) {
                        console.error(
                            `SignalR: ❌ Error in handler "${namedHandler.name}":`,
                            error
                        );
                    }
                });
            } catch (error) {
                console.error(
                    "SignalR: Error handling trip status change:",
                    error
                );
            }
        }
    );
};

async function ensureConnection(email: string, handlerName: string) {
    const hubUrl = process.env.NEXT_PUBLIC_NOTIFICATION_HUB_URL;

    if (!hubUrl) {
        connectionFailed = true;
        notifyBugsnag(new Error("signalr-misconfigured"), {
            kind: "signalr-missing-url",
            handlerName,
        });
        return;
    }

    currentConnectionEmail = email;
    if (handlerName && handlerName !== "Unknown") {
        currentConnectionHandlerName = handlerName;
    }
    clearReconnectTimeout();

    if (isStoppingConnection) {
        return;
    }

    const currentState = getConnectionState();
    if (
        currentState === HubConnectionState.Connected ||
        currentState === HubConnectionState.Connecting ||
        currentState === HubConnectionState.Reconnecting
    ) {
        return;
    }

    if (connection && currentState === HubConnectionState.Disconnected) {
        connection = null;
        connectionPromise = null;
    }

    if (connectionPromise) {
        return;
    }

    connectionFailed = false;
    isConnecting = true;

    const nextConnection = new HubConnectionBuilder()
        .withUrl(`${hubUrl}?userId=${encodeURIComponent(email)}`, {
            skipNegotiation: true,
            transport: HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect(retryDelays)
        .configureLogging(signalRLogger)
        .build();

    attachConnectionHandlers(nextConnection, handlerName, email);
    connection = nextConnection;

    connectionPromise = nextConnection
        .start()
        .then(async () => {
            const recoveryEvent: SignalRReconnectEvent | null =
                retryCount > 0
                    ? {
                          reason: "recovered",
                          retryCount,
                      }
                    : null;

            logger.info("SignalR: Connected successfully");
            retryCount = 0;
            isConnecting = false;
            connectionPromise = null;
            clearOutageState();

            try {
                await nextConnection.invoke("GetConnectionId");
            } catch (error) {
                logger.warn(
                    "SignalR: Failed to register user connection",
                    error
                );
            }

            if (recoveryEvent) {
                notifyReconnectHandlers(recoveryEvent);
            }
        })
        .catch((error) => {
            connection = null;
            connectionPromise = null;
            isConnecting = false;
            retryCount += 1;
            beginOutageTracking({
                source: "start",
                handlerName,
                email,
                error,
            });

            scheduleReconnect(
                retryCount >= maxRetries ? 15_000 : 2_000,
                "start"
            );
        });
}

export function connectToNotificationHub(
    email: string,
    onNotification: (notification: unknown) => void,
    onTripStatusChanged?: (data: {
        tripId: number;
        newStatus: string;
        oldStatus: string | null;
        changedAt: string;
        changedBy?: number;
    }) => void,
    handlerName: string = "Unknown"
) {
    if (!email || !email.includes("@")) {
        return () => undefined;
    }

    const unregister = registerHandlers(
        onNotification,
        onTripStatusChanged,
        handlerName
    );

    void ensureConnection(email, handlerName);

    return unregister;
}

export function registerReconnectHandler(
    handlerName: string,
    handler: (event: SignalRReconnectEvent) => void
) {
    const existingHandler = reconnectHandlers.find(
        (entry) => entry.handler === handler
    );

    if (!existingHandler) {
        reconnectHandlers.push({
            name: handlerName,
            handler,
        });
    }

    return () => {
        reconnectHandlers = removeHandler(reconnectHandlers, handler);
    };
}

export async function disconnectFromNotificationHub() {
    clearReconnectTimeout();
    clearOutageState();

    if (isStoppingConnection) {
        return;
    }

    if (connectionPromise && isConnecting) {
        try {
            await connectionPromise;
        } catch {
            // Ignore connection-start errors while shutting down.
        }
    }

    if (connection) {
        isStoppingConnection = true;

        const state = getConnectionState();
        if (
            state === HubConnectionState.Connected ||
            state === HubConnectionState.Connecting ||
            state === HubConnectionState.Reconnecting
        ) {
            try {
                await connection.stop();
            } catch (error) {
                if (
                    error instanceof Error &&
                    !error.message.includes("HttpConnection before stop()")
                ) {
                    logger.warn("SignalR: Error stopping connection", error);
                }
            }
        }
    }

    connection = null;
    connectionPromise = null;
    isConnecting = false;
    isStoppingConnection = false;

    if (!hasActiveHandlers()) {
        currentConnectionEmail = null;
        currentConnectionHandlerName = null;
    }
}

export function getConnectionStatus() {
    return {
        isConnected: getConnectionState() === HubConnectionState.Connected,
        isConnecting,
        hasFailed: connectionFailed,
        retryCount,
    };
}

export function resetConnectionState() {
    void disconnectFromNotificationHub();
    notificationHandlers = [];
    tripStatusHandlers = [];
    reconnectHandlers = [];
    retryCount = 0;
    connectionFailed = false;
    isConnecting = false;
    isStoppingConnection = false;
    connectionPromise = null;
    currentConnectionEmail = null;
    currentConnectionHandlerName = null;
    clearReconnectTimeout();
    clearOutageState();
}
